import React, { useEffect, useMemo, useRef, useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { ReactComponent as Topheader1 } from '@/assets/img/Topheader-1.svg';
import { useAuth } from '@/lib/auth';
import UserIcon from '@/assets/img/user-default-icon.png';
import './NotificationTab.scss';
import messageRead from '@/assets/img/double-quotes.svg';
import { SUPER_ADMIN_ROLE } from '@/config';
import {
  useGetNotifications,
  Notifications,
  NotificationRes,
  useGetMarkRead,
} from '@/features/auth/api/notifications';
import { OtherMenu7 } from '@/assets/img/other-menu-7';
import { Icon1 } from '@/assets/img/icon-1';
import Icon10 from '@/assets/img/msg-user.svg';
import Icon11 from '@/assets/img/dog-pink.svg';
import Utils from '@/utils/util';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';

type MobileSidebarProps = {
  onMarkRead: () => void;
  unreadCount: any;
};
export const NotificationTab = ({ onMarkRead, unreadCount }: MobileSidebarProps) => {
  const { logout, user } = useAuth();
  const useGetNotificationDetails = useGetNotifications();
  const useMarkAsread = useGetMarkRead();
  const [notificationList, setNotificationList] = useState<Notifications[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (dropdownOpen) {
      getNotifications(1);
    }
  }, [dropdownOpen]);

  const getNotifications = async (pageNumber: number) => {
    if (user) {
      const res = await useGetNotificationDetails.mutateAsync({
        page: pageNumber,
        perPage: 10,
        businessId: user.businessId,
      });
      if (res && res.list) {
        setNotificationList((prevList) =>
          pageNumber === 1 ? res.list : [...prevList, ...res.list]
        );
        setTotalPages(res.totalPages);
      }
    }
  };

  const markAllRead = async () => {
    if (user) {
      const res = await useMarkAsread.mutateAsync({
        businessId: user.businessId,
      });
      if (res) {
        onMarkRead();
        getNotifications(1);
        if (dropdownMenuRef.current) {
          setPage(1);
          dropdownMenuRef.current.scrollTop = 0; // Scroll to top
        }
      }
    }
  };

  const markSingleRead = async (id: any, type: any, data: Notifications) => {
    if (user) {
      const res = await useMarkAsread.mutateAsync({
        businessId: user.businessId,
        isSingleRead: true,
        notificationId: id,
        parentId: type === 'communication' ? data?.parentId : null,
      });
      if (res) {
        onMarkRead();
        getNotifications(1);
        setDropdownOpen(false);
        if (dropdownMenuRef.current) {
          setPage(1);
          dropdownMenuRef.current.scrollTop = 0; // Scroll to top
        }
        navigate(
          type === 'communication'
            ? `./communication?parent-name=${data?.parentName}&parent-id=${data?.parentId}`
            : `./events?selected-tab=${data.appointment?.appointmentStatus}&selected-view=${
                data.appointment?.room ? 'boarding' : 'grooming'
              }&appointment-id=${data?.appointmentId}`
        );
      }
    }
  };

  useEffect(() => {
    if (page > 1) {
      getNotifications(page);
    }
  }, [page]);

  const handleScroll = () => {
    if (dropdownMenuRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = dropdownMenuRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 5 && !loading && page < totalPages) {
        setPage((prevPage) => prevPage + 1);
      }
    }
  };

  useEffect(() => {
    const menu = dropdownMenuRef.current;
    if (menu) {
      menu.addEventListener('scroll', handleScroll);
      return () => {
        menu.removeEventListener('scroll', handleScroll);
      };
    }
  }, [loading, page, totalPages]);

  const isSuperAdmin = user?.businessUserRole?.name === SUPER_ADMIN_ROLE;
  return (
    <div>
      <Dropdown
        className="white header-menu location-dropdown"
        show={dropdownOpen}
        onToggle={() => setDropdownOpen(!dropdownOpen)}
      >
        <Dropdown.Toggle>
          <Topheader1
            onClick={() => {
              setDropdownOpen(true);
              if (dropdownMenuRef.current) {
                dropdownMenuRef.current.scrollTop = 0; // Scroll to top when dropdown is toggled
              }
            }}
          />
        </Dropdown.Toggle>

        <Dropdown.Menu ref={dropdownMenuRef} align={'end'} className="fix-drp-height">
          <div className="notification-headings">
            <span className="not-label">
              Notifications
              {Number(unreadCount ? unreadCount : 0) > 0 && (
                <span className="not-count">
                  <span>{unreadCount}</span>
                </span>
              )}
            </span>
            {notificationList && notificationList.length !== 0 && (
              <div
                className="mark-as-read"
                onClick={() => {
                  markAllRead();
                }}
              >
                Mark all as read
              </div>
            )}
          </div>
          {notificationList && notificationList.length === 0 && (
            <div className="no-notifications"> No Notifications Yet!</div>
          )}
          {!isSuperAdmin &&
            notificationList?.map((cl, index) => (
              <div
                className="location-options add-padding"
                key={index}
                onClick={() => {
                  markSingleRead(cl.id, cl.templateName, cl);
                }}
              >
                <div className="location-left add-padding">
                  <span className="business-img remove-width">
                    {cl.templateName === 'communication' ? (
                      <div className="notification-profile-pic-block">
                        <img
                          className={`notification-profile ${
                            cl?.parent?.imageUrl ? 'parent-profile-img' : ''
                          }`}
                          src={cl?.parent?.imageUrl ? cl?.parent?.imageUrl : Icon10}
                        />
                        <div className="notification-outer">
                          <img src={messageRead} className={`notification-profile-edit`} />
                        </div>
                      </div>
                    ) : (
                      <img
                        className={`msg-icon-img ${
                          cl?.appointment?.pet?.petsImageUrl ? 'pet-profile-img' : ''
                        }`}
                        src={
                          cl?.appointment?.pet?.petsImageUrl
                            ? cl?.appointment?.pet?.petsImageUrl
                            : Icon11
                        }
                      />
                    )}
                  </span>
                  <div className="business-details add-padding">
                    {cl.templateName === 'appointment' ? (
                      <div className="notification-heading">
                        New booking arrived for
                        <span className="notiication-details">
                          {cl?.appointment?.service?.name}
                        </span>{' '}
                        from {cl?.appointment?.petParent?.firstName}
                      </div>
                    ) : cl.templateName === 'appointment_request' ? (
                      <div className="notification-heading">
                        New booking requested for
                        <span className="notiication-details">
                          {cl?.appointment?.service?.name}
                        </span>{' '}
                        from {cl?.appointment?.petParent?.firstName}
                      </div>
                    ) : (
                      <div
                        className={`notification-heading ${
                          cl.templateName === 'communication' ? 'italic' : ''
                        }`}
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(cl.notifyHtml as string),
                        }}
                      />
                    )}
                  </div>
                  {Number(cl.isRead) === 0 && <div className="pink-circle"></div>}
                </div>
                <div className="notification-time">{Utils.timeAgo(cl.createdAt)}</div>
              </div>
            ))}
          <hr />
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};
